/**
 * Responsibility: Adapt ability argument definitions into field-assist form metadata.
 * Scope: UI form-shaping and value normalization only; action execution lives elsewhere.
 */
import type { FormMetaData } from 'field-assist'
import type { Ability } from "@anyquest/shared-core"

type AbilityArg = NonNullable<Ability['args']>[number]
type AbilityArgValues = Record<string, string | number>

function prettifyArgName(name: string): string {
  return String(name || 'value').replace(/[_-]+/g, ' ')
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function inferNumberRange(arg: AbilityArg) {
  const text = `${arg?.name ?? ''} ${arg?.prompt ?? ''}`.toLowerCase()
  const min = Number.isFinite(arg?.min) ? Number(arg.min) : 0

  if (Number.isFinite(arg?.max) && Number.isFinite(arg?.step)) {
    return {
      min,
      max: Number(arg.max),
      step: Math.max(0.01, Number(arg.step)),
      defaultValue: Number.isFinite(arg?.defaultValue) ? Number(arg.defaultValue) : min,
    }
  }

  if (/percent|%/.test(text)) return { min: 0, max: 100, step: 5, defaultValue: 50 }
  if (/distance|range|radius/.test(text)) return { min: 1, max: 30, step: 1, defaultValue: 6 }
  if (/duration|turn|minute|min/.test(text)) return { min: 1, max: 12, step: 1, defaultValue: 3 }
  if (/count|stacks|charges|targets?|hits?|times?/.test(text)) return { min: 1, max: 6, step: 1, defaultValue: 1 }
  return {
    min,
    max: 10,
    step: 1,
    defaultValue: Number.isFinite(arg?.defaultValue) ? Number(arg.defaultValue) : 1,
  }
}

function optionList(arg: AbilityArg, targets: string[]): string[] {
  if (arg.type === 'target') {
    const targetOptions = targets.filter(Boolean)
    return targetOptions.length ? targetOptions : ['Self']
  }
  return (arg.options ?? []).filter(Boolean)
}

function toFieldOptions(options: string[]) {
  return options.map(option => ({ text: option, value: option }))
}

function isMultilineTextArg(arg: AbilityArg): boolean {
  const text = `${arg?.name ?? ''} ${arg?.prompt ?? ''}`.toLowerCase()
  return /message|detail|describe|explain|phrase|sentence|say|bio|story/.test(text)
}

function encodeArgValue(value: string): string {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''
  const cleaned = trimmed.replace(/"/g, "'")
  return /\s/.test(cleaned) ? `"${cleaned}"` : cleaned
}

export function buildAbilityArgInitialValues(
  ability: Ability | null | undefined,
  targets: string[] = [],
): AbilityArgValues {
  const formData: AbilityArgValues = {}
  for (const arg of ability?.args ?? []) {
    if (arg.type === 'target') {
      const options = optionList(arg, targets)
      formData[arg.name] = String(arg.defaultValue ?? options[0] ?? 'Self')
      continue
    }
    if (arg.type === 'choice') {
      const options = optionList(arg, targets)
      formData[arg.name] = String(arg.defaultValue ?? options[0] ?? '')
      continue
    }
    if (arg.type === 'number') {
      const range = inferNumberRange(arg)
      const initial = Number.isFinite(arg.defaultValue) ? Number(arg.defaultValue) : range.defaultValue
      formData[arg.name] = clampNumber(initial, range.min, range.max)
      continue
    }
    formData[arg.name] = String(arg.defaultValue ?? '')
  }
  return formData
}

export function buildAbilityArgFormMeta(
  ability: Ability | null | undefined,
  targets: string[] = [],
): FormMetaData {
  const meta: FormMetaData = {}
  for (const arg of ability?.args ?? []) {
    const label = String(arg.prompt ?? prettifyArgName(arg.name)).trim() || prettifyArgName(arg.name)
    if (arg.type === 'target') {
      meta[arg.name] = {
        label,
        componentType: 'select',
        options: toFieldOptions(optionList(arg, targets)),
        attributes: {
          class: 'arg-select',
        },
      }
      continue
    }
    if (arg.type === 'choice' && optionList(arg, targets).length) {
      meta[arg.name] = {
        label,
        componentType: 'select',
        options: toFieldOptions(optionList(arg, targets)),
        attributes: {
          class: 'arg-select',
        },
      }
      continue
    }
    if (arg.type === 'number') {
      const range = inferNumberRange(arg)
      meta[arg.name] = {
        label,
        componentType: 'input',
        attributes: {
          type: 'range',
          min: String(range.min),
          max: String(range.max),
          step: String(range.step),
          class: 'arg-slider',
        },
      }
      continue
    }
    meta[arg.name] = {
      label,
      componentType: isMultilineTextArg(arg) ? 'textarea' : 'input',
      attributes: isMultilineTextArg(arg)
        ? {
            class: 'arg-input arg-textarea',
            placeholder: `Enter ${prettifyArgName(arg.name).toLowerCase()}...`,
          }
        : {
            type: 'text',
            class: 'arg-input',
            placeholder: `Enter ${prettifyArgName(arg.name).toLowerCase()}...`,
          },
    }
  }
  return meta
}

export function buildAbilityArgSubmission(
  ability: Ability | null | undefined,
  rawValues: Record<string, unknown>,
): { argsText: string; targetLabels: string[] } {
  const args: string[] = []
  const targetLabels: string[] = []

  for (const arg of ability?.args ?? []) {
    const rawValue = rawValues?.[arg.name]
    if (rawValue == null || rawValue === '' || typeof rawValue === 'symbol') continue

    if (arg.type === 'target') targetLabels.push(String(rawValue))

    if (arg.type === 'number') {
      const numeric = Number(rawValue)
      if (!Number.isFinite(numeric)) continue
      args.push(`${arg.name}=${String(numeric)}`)
      continue
    }

    const encoded = encodeArgValue(String(rawValue))
    if (!encoded) continue
    args.push(`${arg.name}=${encoded}`)
  }

  return {
    argsText: args.join(' '),
    targetLabels,
  }
}
